import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppHeader } from '@components/layout/AppHeader';
import { 
  DollarOutlined,
  DownOutlined
} from '@ant-design/icons';
import './styles/faq.css';

interface FAQPriceRow {
  route: string;
  monthly: string;
  perStudent: string;
}

interface FAQItem {
  question: string;
  answer: string;
  priceTable?: FAQPriceRow[];
  priceNote?: string;
}

interface FAQCategory {
  icon: React.ReactNode;
  label: string;
  items: FAQItem[];
}

const FAQ_DATA: FAQCategory[] = [
  {
    icon: <DollarOutlined />,
    label: 'Refund & Cancellation',
    items: [
      {
        question: 'What is the token refund policy?',
        answer:
          'Token Amount is non refundable once service is started.',
      },
      {
        question: 'What are cancellation charges?',
        answer:
          'Cancellation charges are ₹10,000. Refund amount will be calculated after deducting Token Amount, Additional Service charges, Pending Dues, and Cancellation Charges.',
      },
      {
        question: 'What is the trip refund policy?',
        answer:
          'If parents make a personal booking due to driver delay (>20 mins) or driver no-show, they are eligible for a trip refund based on the receipt amount. In case of personal transport, ₹200 will be refunded for that trip.',
      },
      {
        question: 'Are unused trips refundable?',
        answer:
          'Unused trips are not refundable.',
      },
      {
        question: 'What happens if my subscription expires?',
        answer:
          'Subscriptions without active status due to non-payment are subject to immediate termination. Re-activation may take 3–5 days. No trip refunds or compensation will be provided during this period.',
      },
      {
        question: 'How long does a refund take?',
        answer:
          'Once approved, refunds are processed to your bank account within 5–7 business days. Track status anytime from "My Issues".',
      },
      // {
      //   question: 'Can I get a refund for a terminated subscription?',
      //   answer:
      //     'Yes. You are eligible for a prorated refund for unused days. Raise a refund issue and our team will calculate the eligible amount based on your subscription period.',
      // },
      // {
      //   question: 'What if my refund is rejected?',
      //   answer:
      //     'You will see the rejection reason in the issue detail timeline. You can raise a new issue with additional supporting documents.',
      // },
       {
        question: 'Why are my routes and vehicle types changing?',
        answer:
          'If you are on a shared subscription vehicle type will change from 4,6,7,12 based on student density. We club students from same location and optimize routes every week as more students come from a particular location we group them into the same route. A shared subscription starts with a 12 seater route first as long as onward travel time and pickup time are withing estimates during registration.',
      },
       {
        question: 'How do I get a specific type of vehicle for my student?',
        answer:
          'Parents who wish to have a specific vehicle type can upgrade to a private route where the total cost of the route is divided by the parents of the route. Minimum 4 students for a private route, if there are less than 4 students the cost is divided by the remaining students.',
      },
       {
        question: 'What are the prices for private routes?',
        answer:
          'Private routes start with an AC 4 seater for 26 days a month, holidays/cancellations included. This is a fixed cost per month on a single fixed schedule, 2 trips a day. Toll charges are additional. Prices shown are for 4 students; if there are fewer than 4 students the cost is divided by the remaining students.',
        priceTable: [
          { route: 'Upto 10 Kms', monthly: '₹28,000', perStudent: '₹7,000' },
          { route: 'Upto 20 Kms', monthly: '₹32,000', perStudent: '₹8,000' },
          { route: 'Upto 30 Kms', monthly: '₹40,000', perStudent: '₹10,000' },
        ],
        priceNote: 'Prices exclusive of 5% GST. *Discounts applicable as per payment plan (one time or part payment).',
      },
       {
        question: 'Why does it take long to reach home in the evening?',
        answer:
          'Campus departure times are 20mins post campus end time. This is buffer time added given campuses especially in Madhapur do not have parking spaces and vehicles take longer to depart and for students to board once campus ends. Return trips follow the reverse order of the morning trip, we do not have control over evening travel times give many conditions like student delays, traffic, congestion, weather, road blocks etc. If your student is reaching on time in the morning within the estimated pikcup times the issue is not related to routing and due to local traffic and congestion given a shared subscription with multiple stops this is expected behaviour, even travel times can be 1.5x to 2x the travel time of the morning travel time.',
      },      
    ],
  },
];

export const FAQ: React.FC = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState<string | null>(null);

  const toggleFaq = (key: string) => {
    setOpenIndex(prev => (prev === key ? null : key));
  };

  return (
    <div className="faq-page">
      <AppHeader showBack onBack={() => navigate(-1)} />

      <div className="faq-page__container">
        <div className="faq-page__content">
          <div className="faq-page__header">
            <h1 className="faq-page__title">FAQs</h1>
            <p className="faq-page__subtitle">Find answers to common questions</p>
          </div>

          <div className="faq-page__list">
            {FAQ_DATA.map((category, catIdx) => (
              <div key={catIdx}>
                <div className="faq-page__category">
                  <span className="faq-page__category-icon">{category.icon}</span>
                  <span className="faq-page__category-label">{category.label}</span>
                </div>
                {category.items.map((item, itemIdx) => {
                  const key = `${catIdx}-${itemIdx}`;
                  const isOpen = openIndex === key;
                  return (
                    <div
                      key={key}
                      className={`faq-page__item ${isOpen ? 'faq-page__item--open' : ''}`}
                    >
                      <button
                        className="faq-page__question"
                        onClick={() => toggleFaq(key)}
                        aria-expanded={isOpen}
                      >
                        <span>{item.question}</span>
                        <DownOutlined className={`faq-page__arrow ${isOpen ? 'faq-page__arrow--open' : ''}`} />
                      </button>
                      {isOpen && (
                        <div className="faq-page__answer">
                          {item.answer}
                          {item.priceTable && (
                            <div className="faq-page__table-wrap">
                              <table className="faq-page__table">
                                <thead>
                                  <tr>
                                    <th>Route Distance</th>
                                    <th>Monthly</th>
                                    <th>Per Student</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {item.priceTable.map((row, rowIdx) => (
                                    <tr key={rowIdx}>
                                      <td>{row.route}</td>
                                      <td>{row.monthly}</td>
                                      <td>{row.perStudent}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                          {item.priceNote && (
                            <p className="faq-page__table-note">{item.priceNote}</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
